import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { useApolloClient } from 'react-apollo';
import classNames from 'classnames';
import omit from 'lodash.omit';

import FormGroup from '../FormGroup';
import Button from '../Button';
import getConfig from '../../../util/config';
import { splitToFirstLastName } from '../../../util/text';
import Alert from '../Alert';
import { getErrorString } from '../../../util/normalize-errors';
import useCountries from '../../../hooks/useCountries';
import Label from '../Label';
import {
  lookupUsPostCode,
  toLookupablePostCode,
} from '../../../util/postal-code-lookup';
import SearchIcon from '../../icons/SearchIcon';

const propTypes = {
  address: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    address_line_1: PropTypes.string,
    address_line_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    post_code: PropTypes.string,
    country: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
    }),
  }),
  defaultName: PropTypes.string,
  className: PropTypes.string,
  userId: PropTypes.string,
  createAccountAddress: PropTypes.func,
  updateAccountAddress: PropTypes.func,
  onSuccess: PropTypes.func,
  onAddressCreated: PropTypes.func,
  onAddressUpdated: PropTypes.func,
  onCancel: PropTypes.func,
  formLabel: PropTypes.string,
  handlePlatform: PropTypes.bool,
  submitting: PropTypes.bool,
};

const UserAddressForm = ({
  address,
  defaultName,
  className,
  userId,
  createAccountAddress,
  updateAccountAddress,
  onSuccess = () => {},
  onAddressCreated,
  onAddressUpdated,
  onCancel,
  formLabel = 'Shipping Address',
  handlePlatform = true,
  submitting,
}) => {
  const config = getConfig();
  const client = useApolloClient();
  const { countries } = useCountries();
  const [lookingUpPostCode, setLookingUpPostCode] = useState(false);
  const [postCodeNotFound, setPostCodeNotFound] = useState(false);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const initialFormState = useMemo(() => {
    const name = address?.name || defaultName || '';
    const { firstName, lastName } = splitToFirstLastName(name);

    return {
      first_name: firstName,
      last_name: lastName,
      address_line_1: address?.address_line_1 ?? '',
      address_line_2: address?.address_line_2 ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      post_code: address?.post_code ?? '',
      country_id: address?.country?.id ?? config.country.usa.id,
    };
  }, [address, defaultName]);

  const isUsAddress = values => values.country_id === config.country.usa.id;

  const fillFromPostCode = async (values, setFieldValue) => {
    setPostCodeNotFound(false);
    setLookingUpPostCode(true);

    const place = await lookupUsPostCode(client, values.post_code);

    // Bail out if the form went away while the lookup was in flight.
    if (!mounted.current) {
      return;
    }

    setLookingUpPostCode(false);

    if (!place) {
      setPostCodeNotFound(true);
      return;
    }

    if (place.city) {
      setFieldValue('city', place.city);
    }

    if (place.state) {
      setFieldValue('state', place.state);
    }
  };

  const onFormSubmit = async (values, { setSubmitting, setStatus }) => {
    setStatus({ error: '' });

    const data = {
      ...omit(values, ['first_name', 'last_name']),
      name: `${values.first_name} ${values.last_name}`,
    };

    if (!handlePlatform) {
      // Will not deal with GQL creation.
      onSuccess({
        ...address,
        ...data,
      });
      setSubmitting(false);
      return;
    }

    try {
      if (address?.id && updateAccountAddress) {
        const response = await updateAccountAddress({
          account_address_id: address.id,
          ...data,
        });
        setSubmitting(false);
        onSuccess(response.data.accountAddressUpdate.account_address);

        if (onAddressUpdated) {
          onAddressUpdated();
        }
      } else {
        if (createAccountAddress) {
          const response = await createAccountAddress(userId, data);
          onSuccess(response.data.accountAddressCreate.account_address);
          setSubmitting(false);

          if (onAddressCreated) {
            onAddressCreated();
          }
          return;
        }

        setSubmitting(false);
        onSuccess({
          ...address,
          ...data,
        });
      }
    } catch (error) {
      setSubmitting(false);
      setStatus({ error: getErrorString(error) });
    }
  };

  const postCodeLookupNote = (() => {
    if (lookingUpPostCode) {
      return 'Looking up…';
    }

    return postCodeNotFound ? 'We could not find that zip code' : undefined;
  })();

  const classes = classNames({
    'user-address-form': true,
    [className]: !!className,
  });

  return (
    <Formik initialValues={initialFormState} onSubmit={onFormSubmit}>
      {({
        values,
        status = {},
        handleSubmit,
        handleChange,
        handleBlur,
        setFieldValue,
        isSubmitting,
      }) => (
        <form onSubmit={handleSubmit} className={classes}>
          {formLabel && <h4 className="text-center">{formLabel}</h4>}

          <div className="row">
            <div className="col-6">
              <FormGroup
                id="first-name"
                name="first_name"
                type="text"
                label="First Name"
                size="sm"
                value={values.first_name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="col-6">
              <FormGroup
                id="last-name"
                name="last_name"
                type="text"
                label="Last Name"
                size="sm"
                value={values.last_name}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="col-12">
              <FormGroup
                id="address-line-1"
                name="address_line_1"
                type="text"
                label="Address Line 1"
                size="sm"
                value={values.address_line_1}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="col-12">
              <FormGroup
                id="address-line-2"
                name="address_line_2"
                type="text"
                label="Address Line 2"
                size="sm"
                value={values.address_line_2}
                onChange={handleChange}
                onBlur={handleBlur}
              />
            </div>

            <div className="col-md-6 col-12">
              <FormGroup
                id="post-code"
                name="post_code"
                type="text"
                className={isUsAddress(values) ? 'pos-rel' : undefined}
                label={isUsAddress(values) ? 'Zip Code' : 'Post Code'}
                size="sm"
                value={values.post_code}
                onChange={event => {
                  setPostCodeNotFound(false);
                  handleChange(event);
                }}
                onBlur={handleBlur}
                required
                note={
                  isUsAddress(values) && (
                    <Button
                      btnStyle="reset"
                      className="post-code-lookup"
                      title="Find the city and state for this zip code"
                      aria-label="Find the city and state for this zip code"
                      disabled={
                        lookingUpPostCode ||
                        !toLookupablePostCode(values.post_code)
                      }
                      onClick={() => fillFromPostCode(values, setFieldValue)}
                    >
                      <SearchIcon size={14} color="#646e71" />
                    </Button>
                  )
                }
              />

              {isUsAddress(values) && postCodeLookupNote && (
                <p
                  className={classNames({
                    'user-address-post-code-note': true,
                    'text-danger': postCodeNotFound,
                  })}
                >
                  {postCodeLookupNote}
                </p>
              )}
            </div>

            <div className="col-md-6 col-12">
              <div className="form-group">
                <Label id="country">Country</Label>
                <select
                  className="form-control"
                  id="country"
                  name="country_id"
                  value={values.country_id}
                  onChange={event => {
                    setPostCodeNotFound(false);
                    handleChange(event);
                  }}
                  onBlur={handleBlur}
                  required
                >
                  <option value="">Select a country&hellip;</option>
                  {countries.map(country => {
                    return (
                      <option key={country.id} value={country.id}>
                        {country.name}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>

            <div className="col-6">
              <FormGroup
                id="city"
                name="city"
                type="text"
                label="City"
                size="sm"
                value={values.city}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="col-6">
              <FormGroup
                id="state"
                name="state"
                type="text"
                label="State"
                labelTip="State name or abbreviation. Required for US addresses."
                size="sm"
                value={values.state}
                onChange={handleChange}
                onBlur={handleBlur}
                required={isUsAddress(values)}
              />
            </div>

            <div className="col-12">
              {status.error && <Alert type="danger">{status.error}</Alert>}

              <Button
                type="submit"
                btnBlock
                disabled={isSubmitting || submitting}
                className="mt-2"
              >
                {isSubmitting || submitting ? 'Saving…' : 'Save'}
              </Button>

              {onCancel && (
                <Button
                  btnBlock
                  disabled={isSubmitting || submitting}
                  className="mt-1 text-color-base text-center"
                  btnStyle="link"
                  onClick={onCancel}
                >
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

UserAddressForm.propTypes = propTypes;

export default UserAddressForm;
